/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { hasAppFeature, isAndroid, isIos } from '@kcd/app-interface';

import type { OAISchema } from '~/types/openapi';
import { openPageModal } from '~/utils/mobileApp';
import { setStorageData } from '~/utils/storage';

import type { CashPaymentType, PaymentRequestFormType } from './types';

export const isSupportCardPayment = () => {
  // 웹에서의 테스트를 위해 !isAndroid() 추가
  if (isIos() || !isAndroid()) return true;

  // 안드로이드의 경우 지원 가능한 버전을 확인
  return hasAppFeature('이니시스카드결제');
};

/**
 * @description
 * 결제수단이 무통장입금 인지 여부
 * Ex) INICIS_VACCT, INICIS_VACCT_COUPON 등
 */
export type PaymentType =
  OAISchema['MarketPaymentResultResponse']['paymentType'];
export const isCashPaymentType = (paymentType: PaymentType) => {
  if (!paymentType) return false;

  return paymentType?.toLowerCase().includes('vacct');
};

export type AuthMethod =
  OAISchema['MarketPaymentRequestAuthResponse']['method'];
export const startCheckoutModal = (
  url: string,
  method: AuthMethod,
  qs?: Record<string, string>,
) => {
  if (method === 'POST') {
    setStorageData('OPEN_POST_FORM_URL', url);
    openPageModal(
      `/public/app/post-form?url=${encodeURIComponent(
        url,
      )}&${new URLSearchParams({
        ...qs,
      }).toString()}`,
    );
  } else {
    setStorageData('OPEN_GET_FORM_URL', url);
    openPageModal(`/public/app/get-form?url=${url}`);
  }
};

export const createCheckoutDefaultForm = ({
  deliveryLocations,
  originRequestForDelivery,
  needDeliveryLocation,
  cashReceiptType = 'COMPANY',
  ...props
}: OAISchema['MarketCheckoutShippingResponse'] &
  OAISchema['MarketPaymentRefundAccountResponse'] &
  CashPaymentType): PaymentRequestFormType => {
  const { deliveryLocation, requestForDelivery } = (() => {
    const isSelectedDelivery = deliveryLocations?.find(
      ({ isSelected }) => !!isSelected,
    );

    // 이미 이전에 입력한 정보가 있다면 먼저 넣어줌
    if (isSelectedDelivery) {
      return {
        deliveryLocation:
          (isSelectedDelivery.deliveryLocation as PaymentRequestFormType['deliveryLocation']) ||
          'INSIDE_STORE',
        requestForDelivery:
          isSelectedDelivery?.requestForDelivery ||
          originRequestForDelivery ||
          '',
      };
    }

    // 입력폼이 선택사항이면서 안심배송 업데이트 전 입력폼에 값을 넣을 경우
    if (needDeliveryLocation === 'OPTIONAL' && originRequestForDelivery) {
      return {
        deliveryLocation: 'CUSTOM',
        requestForDelivery: originRequestForDelivery,
      };
    }

    return {
      deliveryLocation: 'INSIDE_STORE',
      requestForDelivery: '',
    };
  })();

  const requestDeliveryForm = (() => {
    const obj: Record<
      Exclude<PaymentRequestFormType['deliveryLocation'], 'NONE'>,
      string
    > = {
      REFRIGERATOR: '',
      CUSTOM: '',
      INSIDE_STORE: '',
    };

    deliveryLocations?.forEach((location) => {
      if (location.deliveryLocation === 'NONE') return;
      if (location.deliveryLocation === deliveryLocation) {
        obj[location.deliveryLocation] = requestForDelivery;
        return;
      }
      obj[location.deliveryLocation] = location.requestForDelivery || '';
    });

    return obj;
  })();

  return {
    deliveryLocation,
    requestForDelivery,
    cashReceiptType,
    ...requestDeliveryForm,
    ...props,
  } as PaymentRequestFormType;
};

export const createCheckoutRequestForm = (
  data: PaymentRequestFormType & {
    needDeliveryLocation: OAISchema['MarketCheckoutShippingResponse']['needDeliveryLocation'];
  },
): Omit<
  OAISchema['MarketPaymentRequestAuthRequest'],
  'redirectUrl' | 'failUrl' | 'closeUrl' | 'cartType'
> => {
  const {
    deliveryLocation,
    needDeliveryLocation,
    paymentMethod,
    email,
    vacctBankCode,
    vacctHolderName,
    cashReceiptType,
    cashReceiptNumber,
    registrationNumber,
    point,
    alwaysUsePoint,
  } = data;
  const requestForDelivery = data[deliveryLocation];

  let requestForm: Omit<
    OAISchema['MarketPaymentRequestAuthRequest'],
    'redirectUrl' | 'failUrl' | 'closeUrl' | 'cartType'
  > = (() => {
    const defaultForm = {
      paymentMethod,
      vacctBankCode,
      vacctHolderName,
      cashReceiptType,
      cashReceiptNumber,
      registrationNumber,
      point,
      alwaysUsePoint,
    };

    return needDeliveryLocation !== 'NONE'
      ? { ...defaultForm, requestForDelivery, deliveryLocation }
      : defaultForm;
  })();

  if (email) {
    requestForm = {
      ...requestForm,
      email,
    };
  }

  return requestForm;
};

/**
 * @description
 * KPN 결제인지 체크
 * TODO - KPN으로 완전 전환하면 정리 필요
 */
export const isKPNPaymentType = (
  paymentType: OAISchema['MarketPaymentResultResponse']['paymentType'],
) => {
  if (!paymentType) return false;
  return paymentType?.toLowerCase().includes('kpn');
};

export const isPointPaymentType = (
  paymentType: OAISchema['MarketPaymentResultResponse']['paymentType'],
) => {
  if (!paymentType) return false;
  return paymentType?.toLowerCase().includes('point');
};

type ReturnTypeCalculate = { 만원: number; 천원: number };
export const priceToKoreanMoney = (price: number) => {
  const 만원 = 10000;
  const 천원 = 1000;

  const calculate = (amount: number): ReturnTypeCalculate => {
    if (amount >= 만원) {
      const quotient = Math.floor(amount / 만원);
      // eslint-disable-next-line prettier/prettier
      const remainder = amount - quotient * 만원;

      return {
        만원: quotient + (calculate(remainder).만원 || 0),
        천원: calculate(remainder).천원,
      };
    }

    return {
      만원: 0,
      천원: Math.floor(amount / 천원),
    };
  };

  return calculate(price);
};

// 할인 가능한 금액 노출 텍스트
export const priceToKoreanMoneyToString = (price: number) => {
  const { 만원, 천원 } = priceToKoreanMoney(price);

  if (!만원 && !천원) return '0';

  const 만원result = 만원 > 0 ? `${만원}만` : '';
  const 천원result = 천원 > 0 ? `${천원}천` : '';

  return 만원result + 천원result;
};
